import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import { useSettings } from '@atlas/core';
import { Button, Icon, Loader } from '@atlas/ui';

import { defaultRoutes } from 'components/MainRouter/constants';
import SeoPageContext from 'components/SeoPage/SeoPageContext';

const defaultState = {
  content: null,
  error: null,
  loading: true,
  resolved: false,
};

const { menu } = defaultRoutes;

const StaticPage = ({
  match: {
    params: { slug },
  },
}) => {
  const context = React.useContext(SeoPageContext);

  const [state, setState] = useState(defaultState);
  const [DOMPurify, installDOMPurify] = useState({});
  const { lang } = useSettings();
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    copyStyles: false,
  });

  const handleStaticDataLoaded = useCallback(
    res => {
      setState(state => ({
        ...state,
        content: res.data,
        loading: false,
        resolved: true,
      }));
    },
    [setState],
  );

  const handleStaticDataError = useCallback(
    staticError => {
      const { error } = staticError;

      setState(state => ({
        ...state,
        error,
        loading: false,
        resolved: true,
      }));
    },
    [setState],
  );

  useEffect(() => {
    import('dompurify').then(DOMPurifyModule => {
      installDOMPurify({
        ...DOMPurifyModule.default,
      });
    });
  }, []);

  const makeContent = content => ({ __html: DOMPurify.sanitize(content, { FORCE_BODY: true }) });

  useEffect(() => {
    setState(context.isLoading);
    if (!context.isLoading && context.currentPage?.slug === slug)
      return handleStaticDataLoaded(context.currentPage);
  }, [lang, slug, handleStaticDataError, handleStaticDataLoaded, context]);

  const renderStaticContent = content => (
    <div
      /* eslint-disable-next-line react/no-danger */
      dangerouslySetInnerHTML={makeContent(content)}
    />
  );

  const renderView = () => {
    const { content, error, loading, resolved } = state;

    if (loading || !DOMPurify.sanitize) {
      return (
        <div className="absolute-window">
          <Loader className="absolute" />
        </div>
      );
    }

    if (resolved) {
      if (error) {
        return <div>{error.message}</div>;
      }

      if (content) {
        return renderStaticContent(content);
      }
    }

    return null;
  };

  return (
    <div className="layout-to-scroll">
      <div className="static_page ">
        <div className="back-to-container">
          <Link className="static-page__link" to={menu}>
            <Icon className="icon-back" name="back" />
          </Link>

          <Button className="secondary" label="print" onClick={handlePrint} />
        </div>

        <div
          className="static-page__print"
          ref={componentRef}
          style={{ padding: '16px 16px 75px 16px' }}
        >
          {renderView()}
        </div>
      </div>
    </div>
  );
};

export default StaticPage;
